/*
 * MOD-083 - Globo frame-preview overlay (product page).   (+ MOD-087 snappy apply + mobile scroll)
 *
 * Lays the selected frame's transparent PNG over the product gallery's preview slide and jumps the
 * gallery there, so the customer sees the poster in the chosen frame. The PNG comes from each frame
 * product's overlay metafield, read into a { frame-handle: url } map by
 * snippets/moryarty-frame-overlay.liquid (config injected as the #FrameOverlayConfig JSON blob).
 *
 * The link to Globo is data, not text: every Frame swatch <input> carries `gpo-data-product-handle`
 * = the frame product's handle. "No Frame" has none, so it clears.
 *
 * Triggering (all learned by instrumenting the live page - Globo is awkward here):
 *  - The Frame swatches live OUTSIDE the cart <form>, but their `change` events still bubble to
 *    document, so we listen there.
 *  - When a frame is picked, the change event's TARGET is the chosen swatch input, whose
 *    gpo-data-product-handle is the answer directly. We read it off the target rather than re-querying
 *    Globo's option block, which returns a transient null while it rebuilds (~250-550ms).
 *  - A size change rebuilds the frame block WITHOUT firing a frame `change`, so we also re-resolve the
 *    selection ~800ms after any change, once that rebuild has settled. The last-handle guard makes the
 *    re-resolve a no-op when nothing actually changed, so it never double-jumps.
 *
 * MOD-087 - make the apply feel instant without slowing the page:
 *  - PRELOAD the overlay PNGs, but only after the customer first engages with the product options
 *    (pointer/touch/focus inside the info column) and at low fetch priority, so it never competes with
 *    initial page load / navigation and is skipped for visitors who bounce. Cached => click is instant.
 *  - REVEAL only once the overlay image is loaded AND DECODED (MOD-090; cached/preloaded => same tick), with
 *    no opacity fade, so the gallery never shows the bare black-framed base slide before the frame covers it.
 *    Decoding first is what kills the MAGNETIC-frame flash: those overlays cover the base's black LATERAL
 *    frame with white studio background (rails only top/bottom), so an undecoded-overlay beat used to flash
 *    the black sides before the white covered them. Classic frames hid it (black overlay border over black).
 *  - On MOBILE, re-scroll the framed slide to just below the sticky header. Dawn's setActiveMedia
 *    scrolls the slide top to y=0 (behind the header, MOD-044 makes the gallery flush under it), so the
 *    customer saw only part of the image. Desktop keeps the big image in place (sticky) - no page move.
 */
(function () {
  'use strict';

  function readConfig() {
    var el = document.getElementById('FrameOverlayConfig');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function init(cfg) {
    var MAP = cfg.map || {};
    var NOFRAME = cfg.noFrame || null; // MOD-089: the "No Frame" mask (orientation-matched), or null to just clear
    var POS = parseInt(cfg.position, 10) || 2;
    var SUFFIX = cfg.optionName || 'Frame';
    var DO_JUMP = cfg.jump !== false;
    var frameSel = 'input[data-field-name$="' + SUFFIX + '"]';
    var mqlDesktop = window.matchMedia('(min-width: 750px)');

    // Preload the overlay PNGs so a frame click paints instantly (no flash of the bare base slide).
    // Deferred + gated to the first time the customer touches the product options, and low priority,
    // so it never competes with the initial page load / navigation.
    var preloaded = false;
    function preloadOverlays() {
      if (preloaded) return;
      preloaded = true;
      // Warm the base preview slide image too: it's lazy + display:none until the jump activates it,
      // so on a cold first pick its transparent centre (the poster art under the frame) could paint
      // after the overlay. Promote it to eager (low priority) so the framed result is whole at once.
      var baseSlide = document.querySelectorAll('.product__media-item')[POS - 1];
      var baseImg = baseSlide && baseSlide.querySelector('img');
      if (baseImg && baseImg.loading === 'lazy') {
        baseImg.fetchPriority = 'low';
        baseImg.loading = 'eager';
      }
      Object.keys(MAP).forEach(function (h) {
        var img = new Image();
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.src = MAP[h];
        if (img.decode) img.decode().catch(function () {}); // MOD-090: warm the DECODED cache, not just HTTP, so the on-pick decode is instant
      });
    }
    function onFirstEngage(e) {
      var t = e.target;
      // Fire on the first engagement with the Globo OPTIONS (closer to frame intent than the whole
      // info column), so we don't pull every overlay for visitors who only skim the description.
      if (t && t.closest && t.closest('.gpo-app, .gpo-container, .gpo-element')) {
        preloadOverlays();
        detachEngage();
      }
    }
    function detachEngage() {
      document.removeEventListener('pointerover', onFirstEngage, true);
      document.removeEventListener('focusin', onFirstEngage, true);
      document.removeEventListener('touchstart', onFirstEngage, true);
    }
    document.addEventListener('pointerover', onFirstEngage, true);
    document.addEventListener('focusin', onFirstEngage, true);
    document.addEventListener('touchstart', onFirstEngage, { capture: true, passive: true });

    // Resolve (and lazily create) the overlay <img> inside the preview slide. Re-run each apply so a
    // gallery rebuild can't leave us pointing at a detached node.
    function ensureOverlay() {
      var items = document.querySelectorAll('.product__media-item');
      var slide = items[POS - 1];
      if (!slide) return null;
      var wrap = slide.querySelector('.product__media') || slide;
      var ov = wrap.querySelector('.frame-overlay-img');
      if (!ov) {
        ov = document.createElement('img');
        ov.className = 'frame-overlay-img';
        ov.alt = '';
        ov.setAttribute('aria-hidden', 'true');
        ov.setAttribute('draggable', 'false');
        ov.decoding = 'async';
        if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
        wrap.appendChild(ov);
      }
      return { slide: slide, ov: ov };
    }

    // The frame handle currently selected for the active size (null = No Frame / nothing mapped). Used
    // by the settle-time re-resolve; the change-event path reads the handle off its target instead.
    function currentHandle() {
      var checked = document.querySelectorAll(frameSel + ':checked');
      for (var i = 0; i < checked.length; i++) {
        var block = checked[i].closest('.gpo-element');
        if (block && block.offsetParent !== null) {
          return checked[i].getAttribute('gpo-data-product-handle') || null;
        }
      }
      return null;
    }

    function headerOffset() {
      var hdr = document.querySelector('sticky-header') || document.querySelector('.section-header');
      var h = hdr ? hdr.offsetHeight : 0;
      if (!h) {
        h = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      }
      return h;
    }

    function jump(slide) {
      var g = document.querySelector('media-gallery');
      if (g && typeof g.setActiveMedia === 'function' && slide.dataset.mediaId) {
        try { g.setActiveMedia(slide.dataset.mediaId, false); } catch (e) { /* no-op */ }
      }
      if (mqlDesktop.matches) return; // desktop: the big image is in place (sticky), don't move the page
      // Mobile: override Dawn's y=0 scroll (which hides the slide top behind the sticky header) with a
      // header-aware one. setTimeout(0) lands right after setActiveMedia's own setTimeout(0) (FIFO), in
      // the same task batch, so the two smooth scrolls coalesce into one motion to the right spot — no
      // jitter (a 60ms gap let Dawn's scroll visibly start before this one corrected it).
      window.setTimeout(function () {
        var off = headerOffset();
        var r = slide.getBoundingClientRect();
        if (r.top >= off - 2 && r.bottom <= window.innerHeight + 2) return; // already fully in view
        window.scrollTo({ top: Math.max(0, r.top + window.scrollY - off - 8), behavior: 'smooth' });
      }, 0);
    }

    var last = ' '; // sentinel: the first resolved state always applies once
    var userPicked = false; // MOD-095: true only after a real Frame-swatch pick — gates the No-Frame mask
    function applyHandle(h, allowJump) {
      var refs = ensureOverlay();
      if (!refs) return;
      // Frame handle -> its overlay. "No Frame" (h null) -> the No-Frame mask, but ONLY after the user has
      // actually picked a frame (allowJump && userPicked). MOD-089 applied it on any allowJump; MOD-095 added
      // userPicked so an on-load `change` (variant input, the hidden-size fix, or a Globo rebuild) on a product
      // that DEFAULTS to "No Frame" can no longer reveal the mask without a real click. Initial load: plain base.
      var url = h ? MAP[h] : (allowJump && userPicked ? NOFRAME : null);
      var key = h || (url ? '__NOFRAME__' : '__CLEAR__'); // dedup key that separates No-Frame from cleared
      if (key === last) return; // unchanged - ignore the rest of Globo's event storm
      last = key;
      if (!url) {
        refs.ov.classList.remove('is-visible'); // clear, leave the gallery put
        return;
      }
      var done = false;
      function reveal() {
        if (done || last !== key) return; // already shown, or a newer pick superseded this one
        done = true;
        refs.ov.classList.add('is-visible');
        if (allowJump && DO_JUMP) jump(refs.slide);
      }
      refs.ov.onerror = function () { if (last === key) refs.ov.classList.remove('is-visible'); }; // bad URL: clear, never paint a broken frame
      if (refs.ov.getAttribute('src') !== url) refs.ov.src = url;
      // MOD-090: DECODE the overlay before revealing + jumping, so its bitmap is paint-ready and composites in
      // the SAME frame as the base slide. Otherwise the base (poster in a black frame) paints a beat before the
      // overlay covers it — invisible for classic frames (black over black) but a visible flash of the black
      // LATERAL frame for MAGNETIC frames (white overlay sides). decode() on the preloaded PNG is ~instant;
      // reveal() stays guarded by last!==key so a superseded pick is a no-op.
      if (refs.ov.decode) {
        refs.ov.decode().then(reveal).catch(function () {
          if (refs.ov.complete && refs.ov.naturalWidth) reveal(); else refs.ov.onload = reveal; // decode aborted/unsupported: fall back to load-gated reveal
        });
      } else {
        refs.ov.onload = reveal;
        if (refs.ov.complete && refs.ov.naturalWidth) reveal(); // cached/preloaded: reveal + jump now
      }
    }

    applyHandle(currentHandle(), false); // initial state, never yank the gallery on load

    var reT;
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches(frameSel)) {
        userPicked = true; // MOD-095: a real Frame-swatch pick — the No-Frame mask may now apply
        applyHandle(t.getAttribute('gpo-data-product-handle') || null, true);
      }
      // MOD-095: only (re)arm the ~800ms settle re-resolve for changes INSIDE the Globo option area — a size
      // change rebuilds the frame block WITHOUT firing a frame `change`, which is what the settle re-resolve is
      // for. This stops unrelated on-load `change` events elsewhere (variant inputs, the hidden-size fix) from
      // scheduling applyHandle(currentHandle(), true) and applying No-Frame on a No-Frame-default product.
      if (t && t.closest && t.closest('.gpo-app, .gpo-container, .gpo-element')) {
        clearTimeout(reT);
        reT = setTimeout(function () { applyHandle(currentHandle(), true); }, 800);
      }
    });
  }

  // Boot once Globo has rendered its options (or give up after a few seconds).
  function boot() {
    var cfg = readConfig();
    if (!cfg) return;
    var start = Date.now();
    (function poll() {
      if (document.querySelector('.gpo-element') || Date.now() - start > 8000) init(cfg);
      else setTimeout(poll, 250);
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
